import React, { useRef } from 'react';
import {
  Box, IconButton, Typography, Tooltip, Popover,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { ShortlistHistoryEntry, historyTitle, formatHistoryTime, modeLabel } from '../utils/history';

interface Props {
  history: ShortlistHistoryEntry[];
  onSelect: (entry: ShortlistHistoryEntry) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
  onExport: () => void;
  onImport: (jsonText: string) => void;
  isDark: boolean;
}

const HistoryPopover: React.FC<Props> = ({
  history, onSelect, onDelete, onClear, onExport, onImport, isDark,
}) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => importInputRef.current?.click();

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text === 'string') onImport(text);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSelect = (entry: ShortlistHistoryEntry) => {
    onSelect(entry);
    setAnchor(null);
  };

  const handleClear = () => {
    onClear();
    setAnchor(null);
  };

  return (
    <>
      <Tooltip title="History" arrow>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          size="small"
          sx={{
            color: anchor ? 'primary.main' : 'text.secondary',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
            borderRadius: '10px', width: 34, height: 34,
          }}
        >
          <HistoryIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 340,
              maxWidth: 'calc(100vw - 2rem)',
              p: 1.25,
              borderRadius: '14px',
              mt: 1,
              background: isDark ? 'rgba(20,15,12,0.96)' : 'rgba(255,248,239,0.97)',
              backdropFilter: 'blur(20px)',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 12px 40px rgba(79,48,29,0.18)',
            },
          },
        }}
      >
        <input ref={importInputRef} type="file" accept=".json" hidden onChange={handleImportFile} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pt: 0.25, mb: 1 }}>
          <Typography sx={{
            fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: 'text.secondary',
          }}>
            History
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title="Import history from file" arrow>
              <IconButton size="small" onClick={handleImportClick} sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'primary.main' } }}>
                <FileUploadIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export history to file" arrow>
              <IconButton size="small" onClick={onExport} sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'primary.main' } }}>
                <FileDownloadIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            {history.length > 0 && (
              <Typography
                component="button"
                onClick={handleClear}
                sx={{
                  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em',
                  color: 'text.secondary', background: 'none', border: 'none',
                  padding: '4px 6px', cursor: 'pointer', ml: 0.25, borderRadius: '4px',
                  '&:hover': { color: '#EF4444' },
                }}
              >
                Clear all
              </Typography>
            )}
          </Box>
        </Box>

        {history.length === 0 ? (
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', px: 0.75, py: 1 }}>
            Analyzed batches will appear here.
          </Typography>
        ) : (
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 0.5,
            maxHeight: 400, overflowY: 'auto', pr: 0.5, mr: -0.5,
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)', borderRadius: 3 },
          }}>
            {history.map((entry) => (
              <Box
                key={entry.id}
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(entry)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(entry); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1, py: 0.85, borderRadius: '12px',
                  color: 'text.secondary', cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                    color: 'text.primary',
                  },
                  '&:hover .delete-btn': { opacity: 1 },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{
                    fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.35,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'inherit',
                  }}>
                    {historyTitle(entry)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.68rem', color: 'text.disabled',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {modeLabel(entry.mode)} · {entry.cvCount} CV{entry.cvCount > 1 ? 's' : ''} · {formatHistoryTime(entry.timestamp)}
                  </Typography>
                </Box>
                <Tooltip title="Delete from history" arrow>
                  <IconButton
                    size="small"
                    className="delete-btn"
                    onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
                    sx={{
                      flexShrink: 0, opacity: 0, color: 'text.disabled',
                      transition: 'opacity 0.15s ease, color 0.15s ease',
                      '&:hover': { color: '#EF4444' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default HistoryPopover;
